import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import DEFAULTVALUE from '@/app/constants/defaultValue';
import { SubGroup } from '@/app/models/financalGroup';
import { TransferDetailBase } from '@/app/models/transferDetail';
import { useConsumableTransferMutation } from '@/app/services/detailsConsumableApi';
import { formatDateForBackend } from '@/app/utils/dateUtils';
import {
  cpfOrCpnjFromBack,
  formatCurrency,
  formatHandleCPFAndCNPJ,
  isSupplierCodeValid,
} from '@/app/utils/numberUtils';
import FormFieldDate from '@/components/common/DateField';
import DrawerButtons from '@/components/common/DrawerButtons';
import { usePeriod } from '@/context/PeriodContext';
import useFormattedCurrency from '@/hooks/useFormattedCurrency';
import { Box, useTheme } from '@mui/material';
import { toast } from 'react-toastify';
import FormField from '../baseForms/FormField';
import FormFieldLong from '../baseForms/FormFieldLong';
import FormFieldQuantity from '../baseForms/FormFieldQuantity';

interface NewConsumableTransferProps {
  selectedItem: TransferDetailBase;
  setDrawerOpen: (open: boolean) => void;
  selectedFinancialSubGroup: SubGroup;
}

export default function NewConsumableTransfer(props: NewConsumableTransferProps) {
  const { t } = useTranslation(['consumable', 'common']);
  const theme = useTheme();
  const { selectedPeriod } = usePeriod();
  const { selectedItem, setDrawerOpen, selectedFinancialSubGroup } = props;

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const { value: totalValue, setValue: setTotalValue } = useFormattedCurrency(selectedItem?.newDetail.value);
  const [justification, setJustification] = useState<string>('');
  const [description, setDescription] = useState<string>(
    selectedItem?.newDetail.description != null ? selectedItem?.newDetail.description : '',
  );
  const [quantity, setQuantity] = useState<number | null>(null);

  const [supplier, setSupplier] = useState<string>(
    selectedItem?.newDetail.supplier != null ? selectedItem?.newDetail.supplier : '',
  );

  const [supplierCode, setSupplierCode] = useState<string>('');

  const [invoiceCode, setInvoiceCode] = useState<string>('');
  const [expenseDate, setExpenseDate] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  };

  const [transfer] = useConsumableTransferMutation();

  const handleSubmit = async () => {
    if (!selectedItem) return;
    setDrawerOpen(false);

    await toast.promise(
      transfer({
        id: selectedItem.id,
        originFinancialGroupCode: selectedItem.originFinancialGroupCode as string,
        financialSubGroupId: selectedFinancialSubGroup.id as number,
        period: selectedPeriod?.date as string,
        newDetail: {
          description: description || '',
          value: parseFloat(totalValue.replace('R$', '').replace('.', '').replace(',', '.')) || DEFAULTVALUE,
          quantity: quantity || 0,
          invoiceCode: invoiceCode || '',
          supplier: supplier || '',
          supplierCode: cpfOrCpnjFromBack(supplierCode),
          justify: justification,
          expenseDate: formatDateForBackend(expenseDate as string),
        },
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('common:success.transfer') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const hasNegative = val.includes('-');
    const cleaned = val.replace(/[^0-9]/g, '');
    const raw = hasNegative ? `-${cleaned}` : cleaned;
    setTotalValue(formatCurrency(raw));
  };

  const handleSupplierCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatHandleCPFAndCNPJ(e.target.value);
    setSupplierCode(formatted);
  };
  const supplierCodeIsValid = isSupplierCodeValid(supplierCode);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(80vh - 220px)',
        maxHeight: 'calc(80vh - 220px)',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mt: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        />
        <Box sx={{ display: 'block', minWidth: theme.spacing(31), mb: 2 }}>
          <FormField label={t('common:description') as string} value={description} onChange={handleChange} />
          <FormFieldQuantity label={t('common:quantity')} value={quantity} onChange={setQuantity} />
          <FormField label={t('common:total.value') as string} value={totalValue} onChange={handleValueChange} />
          <FormField
            label={t('common:supplier') as string}
            value={supplier}
            onChange={(e) => {
              setSupplier(e.target.value);
            }}
          />

          <FormField
            label={t('common:cnpj') as string}
            value={supplierCode}
            onChange={handleSupplierCode}
            error={!supplierCodeIsValid}
          />

          <FormField
            label={t('common:invoice.number') as string}
            value={invoiceCode}
            onChange={(e) => {
              setInvoiceCode(e.target.value);
            }}
          />
          <FormFieldDate label={t('common:expenseDate')} value={expenseDate as string} onChange={setExpenseDate} />

          <FormFieldLong
            label={t('common:justification')}
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
          />
        </Box>
      </Box>
      <Box sx={{ mt: 'auto', mb: 4 }}>
        <DrawerButtons
          onSave={() => {
            handleSubmit();
          }}
          onCancel={handleCancel}
          labelSave={t('common:transfer') as string}
          disabled={!supplierCodeIsValid || !expenseDate}
        />
      </Box>
    </Box>
  );
}
